import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import Typography from "@mui/material/Typography";
import {
  makeDELETERequestOnUserServiceWithQueryParameter,
  makeGETRequestOnUserService,
  makePOSTRequestOnUserService,
} from "../../Api/api";
import toastr from "toastr";
import { ActionButtons, OrderDetailsInterface } from "./interfaces";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { StyledButton } from "./NewRFQOrderDetails";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

const RazorpayActionButtons = ({
  orderDetails,
  orderUUID,
  setLoading,
  type,
}: {
  orderDetails: OrderDetailsInterface;
  orderUUID: string;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  type: string;
}) => {
  const [transferInfoOpen, setTransferInfoOpen] = React.useState(false);
  const [transferInfoData, setTransferInfoData] = React.useState<any>(null);
  const [transferInfoLoading, setTransferInfoLoading] = React.useState(false);

  const cancelPayment = () => {
    let apiUrl = `order_management_v2/razorpay/payment_link/cancel/${orderUUID}/`;
    if (type === "partner") {
      apiUrl += "?user_type=partner";
    } else if (type === "client") {
      apiUrl += "?user_type=client";
    }
    makeDELETERequestOnUserServiceWithQueryParameter(apiUrl)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("Payment Link Cancelled Successfully");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const checkOrderStatus = () => {
    let apiUrl = `order_management_v2/razorpay/order_link/get/${orderUUID}/`;
    if (type === "partner") {
      apiUrl += "?user_type=partner";
    } else if (type === "client") {
      apiUrl += "?user_type=client";
    }
    makeGETRequestOnUserService(apiUrl)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("Order Status Fetched Successfully");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const checkPaymentStatus = () => {
    let apiUrl = `order_management_v2/razorpay/payment_link/get/${orderUUID}/`;
    if (type === "partner") {
      apiUrl += "?user_type=partner";
    } else if (type === "client") {
      apiUrl += "?user_type=client";
    }
    makeGETRequestOnUserService(apiUrl)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("Payment Status Fetched Successfully");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };
  const generatePaymentLink = () => {
    let apiUrl = `order_management_v2/razorpay/payment_link/generate/${orderUUID}/`;
    if (type === "partner") {
      apiUrl += "?user_type=partner";
    } else if (type === "client") {
      apiUrl += "?user_type=client";
    }
    makePOSTRequestOnUserService(apiUrl)
      .then((response) => {
        if (response && response.status === 200) {
          toastr.success("Payment Link Generated Successfully");
          setLoading(true);
        }
      })
      .catch((error) => toastr.error(error));
  };

  const razorpayTransfer = async () => {
    try {
      let apiUrl = "";

      if (
        orderDetails?.razorpay_fe_order_id &&
        orderDetails?.payment_status === "paid"
      ) {
        apiUrl =
          type === "partner"
            ? `order_management_v2/razorpay/create/order/transfer_link/${orderUUID}/?user_type=partner`
            : type === "client"
              ? `order_management_v2/razorpay/create/order/transfer_link/${orderUUID}/?user_type=client`
              : `order_management_v2/razorpay/create/order/transfer_link/${orderUUID}/`;
      }
      else if (orderDetails?.payment_link && orderDetails?.razorpay_order_id) {
        apiUrl =
          type === "partner"
            ? `order_management_v2/razorpay/create/payment_transfer/${orderUUID}/?user_type=partner`
            : type === "client"
              ? `order_management_v2/razorpay/create/payment_transfer/${orderUUID}/?user_type=client`
              : `order_management_v2/razorpay/create/payment_transfer/${orderUUID}/`;
      }
      else {
        toastr.warning("Razorpay transfer conditions not satisfied");
        return;
      }

      const resp = await makePOSTRequestOnUserService(apiUrl);

      if (resp.status === 200) {
        const amount = resp?.data?.amount;
        const isRBI = amount >= 200000;
        const accountId = isRBI
          ? "acc_ORM0MKBcTP2Rsu"
          : "acc_ORLuakXTNs1ga2";

        const accountName = isRBI
          ? "RBI Account of ICCL"
          : "HDFC Account of ICCL";

        toastr.success(
          `Transfer Successfully in ${accountName} and id is ${accountId}`
        );
        setLoading(true);
      }
    } catch (error) {
      toastr.error("Failed to process Razorpay transfer");
      console.error(error);
    }
  };

  const fetchTransferInfo = async () => {
    setTransferInfoOpen(true);
    setTransferInfoLoading(true);
    let apiUrl = `order_management_v2/razorpay/get/transfer_details/${orderUUID}/`;
    if (type === "partner") {
      apiUrl += "?user_type=partner";
    } else if (type === "client") {
      apiUrl += "?user_type=client";
    }
    try {
      const resp = await makeGETRequestOnUserService(apiUrl);

      if (resp && resp.status === 200) {
        setTransferInfoData(resp.data);
      }
    } catch (error) {
      toastr.error("Failed to fetch transfer details");
    } finally {
      setTransferInfoLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} 
  ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const formatAmount = (amount: number) => {
    if (!amount) return "0.00";
    const rupees = amount / 100;
    return rupees.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getBankName = (account: string) => {
    if (account === "acc_ORLuakXTNs1ga2") return "HDFC Account";
    if (account === "acc_ORM0MKBcTP2Rsu") return "RBI Account";
    return "Unknown Bank";
  };

  const razorpayActionButtons: ActionButtons[] = useMemo(
    () => [
      {
        name: "Cancel T1 Payment",
        color: "error",
        icon: <DeleteForeverRoundedIcon />,
        disabled:
          (!!orderDetails?.payment_link &&
            orderDetails?.payment_status === "paid") ||
          ["cancelled", "paid", ""].includes(orderDetails?.payment_status!),
        helperText:
          "Only works when payment link generated but payment not done",
        handleClick: () => cancelPayment(),
      },
      {
        name: "Check T1 Payment Status",
        color: "success",
        icon: <AutorenewOutlinedIcon />,
        disabled:
          !!!orderDetails?.payment_link ||
          ["cancelled", "paid"].includes(orderDetails?.payment_status!),
        handleClick: () => checkPaymentStatus(),
      },
      {
        name: "Check T1 Order Status",
        color: "success",
        icon: <AutorenewOutlinedIcon />,
        disabled:
          !!!orderDetails?.razorpay_fe_order_id ||
          !!orderDetails?.payment_link ||
          orderDetails?.payment_status === "paid",
        handleClick: () => checkOrderStatus(),
      },
      {
        name: "Generate T1 Payment Link",
        color: "secondary",
        icon: <AccountBalanceRoundedIcon />,
        disabled:
          !!orderDetails?.payment_link ||
          orderDetails?.payment_status === "paid",
        handleClick: () => generatePaymentLink(),
      },
      ...(orderDetails?.payment_process?.toUpperCase() === "PAYMENT GATEWAY"
        ? [
          {
            name: "Razorpay Transfer",
            color: "secondary",
            icon: <AccountBalanceRoundedIcon />,
            handleClick: razorpayTransfer,
            disabled: !!orderDetails?.razorpay_transfer_order_id,
            helperText: orderDetails?.razorpay_transfer_order_id ? (
              <>
                Razorpay transfer completed. Click (i) to view details.
                <IconButton
                  color="info"
                  size="small"
                  sx={{ ml: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchTransferInfo();
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              ""
            ),
          }
        ]
        : []),
    ],
    [orderDetails]
  );
  return (
    <Grid container spacing={2} mt={4}>
      {razorpayActionButtons.map((button, index) => (
        <Grid item key={index} xs={6} sm={4} md={"auto"}>
          <StyledButton
            startIcon={button.icon}
            color={button.color}
            onClick={button.handleClick}
            disabled={button.disabled}
          >
            {button.name}
          </StyledButton>
          <Typography
            sx={{
              color: "#45E142",
              fontSize: 12,
              textAlign: "center",
              mt: 1,
            }}
          >
            {button.helperText}
          </Typography>
        </Grid>
      ))}

      <Dialog open={transferInfoOpen} onClose={() => setTransferInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Details</DialogTitle>
        <DialogContent>
          {transferInfoLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={150}>
              <CircularProgress />
            </Box>
          ) : (
            transferInfoData && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Transfer ID</Typography>
                  <Typography>{transferInfoData.id}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Typography>{transferInfoData.status}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Currency</Typography>
                  <Typography>{transferInfoData.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Amount</Typography>
                  <Typography>₹ {formatAmount(transferInfoData.amount)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Recipient Account</Typography>
                  <Typography>
                    {transferInfoData.recipient}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "#45E142"
                  }}>
                    {getBankName(transferInfoData.recipient)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Settlement Status</Typography>
                  <Typography>{transferInfoData.settlement_status}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">On Hold</Typography>
                  <Typography>{transferInfoData.on_hold ? "Yes" : "No"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Source Payment</Typography>
                  <Typography>{transferInfoData.source}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Created At</Typography>
                  <Typography>{formatDate(transferInfoData.created_at)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Processed At</Typography>
                  <Typography>{formatDate(transferInfoData.processed_at)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Notes</Typography>
                  <Typography>
                    {transferInfoData.notes?.name} - {transferInfoData.notes?.branch}
                  </Typography>
                </Grid>
              </Grid>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default RazorpayActionButtons;